/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page } from 'react-pdf/dist/entry.webpack';
import HTMLFlipBook from 'react-pageflip';
import cmsUtils from '../../../utils/cmsUtils';
import utils from '../../../../utils';
import * as s from './flipbookStyle';

const SPREAD_MIN_WIDTH = 760;  // stage width at which we place two pages side by side
const MIN_PAGE_WIDTH = 120;

export function Flipbook(props) {
  const item = props.item;
  const cmsOption = props.cmsOption || {};

  // Media is served by the backend, which is a separate origin in development,
  // so the stored path needs the resource base in front of it - same as every
  // other media-bearing item does.
  const pdfPath = (cmsUtils.payload(item, 'PdfUrl') || '').trim();
  const pdfUrl = pdfPath ? utils.site.resourcePath(pdfPath) : '';
  const showCover = (cmsUtils.payload(item, 'ShowCover') || 'true') === 'true';

  const [numPages, setNumPages] = useState(null);
  // Stays null until read off the PDF. The book is not mounted before then,
  // because StPageFlip only reads width/height when it is constructed.
  const [pageRatio, setPageRatio] = useState(null);
  const [stageW, setStageW] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // iPhone Safari has no Fullscreen API for ordinary elements - only <video>
  // gets webkitEnterFullscreen - so there we pin the shell over the viewport
  // with CSS instead. Both paths look the same to the rest of the component.
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false);
  const fullscreen = isFullscreen || pseudoFullscreen;
  const [orientation, setOrientation] = useState('landscape');
  const [pageInput, setPageInput] = useState('');

  const shellRef = useRef(null);
  const stageRef = useRef(null);
  const flipRef = useRef(null);
  const anchorRef = useRef(null);
  // Created once. Only ever *moved* between mounts - never swapped for another
  // node, and never conditionally portalled - so React keeps its contents
  // mounted and the PDF is not reloaded when fullscreen toggles.
  const [portalEl] = useState(() =>
    typeof document !== 'undefined' ? document.createElement('div') : null);
  // The book remounts on resize, but the #p= deep link should only apply once.
  const hashAppliedRef = useRef(false);
  const flipLockRef = useRef(false);
  const suppressHashRef = useRef(false);

  useEffect(() => {
    setNumPages(null);
    setPageRatio(null);
    setCurrentPage(0);
    setPageInput('');
    flipLockRef.current = false;
    hashAppliedRef.current = false;
  }, [pdfUrl]);

  const onDocumentLoad = useCallback((pdf) => {
    setNumPages(pdf.numPages);
    // Sample an interior page, not the cover: a square cover in front of 2-up
    // spreads is common, and it is the body pages the book box has to fit.
    const sample = pdf.numPages > 1 ? 2 : 1;
    pdf.getPage(sample).then(p => {
      // getViewport applies page rotation; p.view is a coordinate box, not a size.
      const vp = p.getViewport({ scale: 1 });
      if (vp.width > 0 && vp.height > 0) setPageRatio(vp.height / vp.width);
      else setPageRatio(297 / 210);
    }).catch(() => setPageRatio(297 / 210));
  }, []);

  // Only the width is measured. The height is computed from it and applied back
  // to the shell, so the frame hugs the book instead of the two chasing each
  // other through the layout.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    let raf = null;
    const measure = () => {
      const w = el.clientWidth;
      const vh = window.innerHeight;
      setStageW(prev => Math.abs(prev - w) < 2 ? prev : w);
      setViewportH(prev => Math.abs(prev - vh) < 2 ? prev : vh);
    };
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResize);
      ro.observe(el);
    }
    window.addEventListener('resize', onResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [pdfUrl, fullscreen]);

  // #portal-root is a sibling of #root (public/index.html), so nothing in the
  // app's DOM can trap the shell in a stacking context - which is what no
  // z-index could fix. Same mount the app's other overlays use, see
  // site/portal/portal.js.
  useEffect(() => {
    if (!portalEl) return;
    const mount = pseudoFullscreen
      ? document.getElementById('portal-root')
      : anchorRef.current;
    if (mount && portalEl.parentNode !== mount) mount.appendChild(portalEl);
  }, [pseudoFullscreen, portalEl]);

  useEffect(() => () => {
    if (portalEl && portalEl.parentNode) portalEl.parentNode.removeChild(portalEl);
  }, [portalEl]);

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const flipCorner = 'bottom';

  // Known library defect: below the two-page breakpoint the backward flip does
  // not animate, because page-flip aims its simulated corner drag at a bare
  // x:10 without adding rect.left (its forward flip does add it). Calling the
  // internal flip directly to correct that was tried and did not work, and it
  // skipped flipToPage's setCurrentSpreadIndex bookkeeping, which then broke
  // the forward flip. Staying on the library's own path until it can be
  // diagnosed from runtime values. See _jaba/flipbook/FLIPBOOK-PLAN.md.
  // Use the library's own spread-wise stepping, not flip(index). A spread holds
  // two pages ([[0],[1,2],[3,4],...] with a cover), and flipToPage acts only
  // when the target's spread index differs from the current one - so flip(cur+1)
  // from the left page of a spread targets its own right page and does nothing.
  const flipPrev = useCallback(() => {
    if (flipLockRef.current) return;
    const pf = flipRef.current && flipRef.current.pageFlip();
    if (pf) pf.flipPrev(flipCorner);
  }, []);

  const flipNext = useCallback(() => {
    if (flipLockRef.current) return;
    const pf = flipRef.current && flipRef.current.pageFlip();
    if (pf) pf.flipNext(flipCorner);
  }, []);
  const goHome = useCallback(() => {
    if (flipRef.current) flipRef.current.pageFlip().flip(0);
  }, []);

  // turnToPage jumps without animating, which is what a page-number box wants.
  const turnToPage = useCallback((index) => {
    if (flipRef.current) flipRef.current.pageFlip().turnToPage(index);
  }, []);

  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
    // Restoring a deep link on load is not a page turn the reader made, so it
    // must not stamp the URL before the router has settled.
    if (suppressHashRef.current) { suppressHashRef.current = false; return; }
    try {
      // Spell out the path. A bare '#p=2' is resolved against the document base
      // URL, so anything that shifts that base writes '/#p=2' and loses the
      // page - which then survives into the next hard refresh as a real
      // navigation to the site root. replaceState does not fire popstate, so
      // react-router stays out of this either way.
      const { pathname, search } = window.location;
      window.history.replaceState(null, '', pathname + search + '#p=' + (e.data + 1));
    } catch (ex) { /* hash is a nicety, never fail the flip over it */ }
  }, []);

  const onChangeState = useCallback((e) => {
    flipLockRef.current = e.data === 'flipping';
  }, []);
  const onChangeOrientation = useCallback((e) => setOrientation(e.data), []);

  const onInit = useCallback((e) => {
    // The book remounts on any geometry change. If that happens mid-flip the
    // old instance never reports 'read', so a lock set by the previous
    // instance would stay set and kill both buttons. Clear it per instance.
    flipLockRef.current = false;
    setOrientation(e.data.mode);
    if (hashAppliedRef.current) return;
    hashAppliedRef.current = true;
    const m = /(?:^|[#&])p=(\d+)/.exec(window.location.hash || '');
    if (!m) return;
    const target = parseInt(m[1], 10) - 1;
    const total = flipRef.current ? flipRef.current.pageFlip().getPageCount() : 0;
    if (target >= 0 && target < total) {
      suppressHashRef.current = true;
      turnToPage(target);
      setCurrentPage(target);
    }
  }, [turnToPage]);

  const submitPageInput = useCallback((e) => {
    e.preventDefault();
    const n = parseInt(pageInput, 10);
    if (!isNaN(n) && numPages && n >= 1 && n <= numPages) {
      turnToPage(n - 1);
      setCurrentPage(n - 1);
    }
    setPageInput('');
    if (e.target && e.target.blur) e.target.blur();
  }, [pageInput, numPages, turnToPage]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el || !numPages) return;
    const onKey = (e) => {
      if (document.activeElement && document.activeElement.tagName === 'INPUT') return;
      const inShell = el.contains(document.activeElement) || fullscreen;
      if (!inShell) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); flipPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); flipNext(); }
      else if (e.key === 'Home') { e.preventDefault(); goHome(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [numPages, fullscreen, flipPrev, flipNext, goHome]);

  const toggleFullscreen = () => {
    const el = shellRef.current;
    if (!el) return;

    const enter = el.requestFullscreen || el.webkitRequestFullscreen;
    const exit = document.exitFullscreen || document.webkitExitFullscreen;
    const native = document.fullscreenElement || document.webkitFullscreenElement;

    if (pseudoFullscreen) { setPseudoFullscreen(false); return; }

    if (!native) {
      // Feature-detect rather than assume: calling .call() on an absent method
      // is what made this button dead on iPhone.
      if (typeof enter === 'function') {
        try { enter.call(el); } catch (ex) { setPseudoFullscreen(true); }
      } else {
        setPseudoFullscreen(true);
      }
    } else if (typeof exit === 'function') {
      exit.call(document);
    }
  };

  // No native fullscreen means no browser-provided way out, so Escape and a
  // locked background have to be supplied here.
  useEffect(() => {
    if (!pseudoFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setPseudoFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [pseudoFullscreen]);

  const cssClass = utils.classNames('cms_item', 'flipbook', item.cssClass || item.anchorName || '');

  const gap = stageW >= 768 ? 24 : 16;
  const twoUp = stageW >= SPREAD_MIN_WIDTH;
  const cols = twoUp ? 2 : 1;
  const usableW = pdfUrl ? Math.max(0, stageW - gap * 2) : 0;
  const maxBookH = Math.max(200,
    Math.round((viewportH || 800) * (fullscreen ? 1 : 0.86)) - s.TOOLBAR_HEIGHT - gap * 2);
  const hasBox = usableW > 0 && !!pageRatio;
  const pageW = hasBox
    ? Math.max(MIN_PAGE_WIDTH, Math.floor(Math.min(usableW / cols, maxBookH / pageRatio)))
    : 0;
  const pageH = pageW ? Math.round(pageW * pageRatio) : 0;

  // Stable children — recreating pages on every flip triggers updateFromHtml and
  // leaves torn-off flip layers (grey strip on the spread).
  const pdfPages = useMemo(() => {
    if (!numPages || !pageH) return null;
    return Array.from({ length: numPages }, (_, i) => (
      <div key={i} className="flipbook-page" css={s.page}>
        <Page
          pageNumber={i + 1}
          height={pageH}
          renderAnnotationLayer={false}
          renderTextLayer={false}
          loading=""
        />
      </div>
    ));
  }, [numPages, pageH]);

  if (cmsOption.isCmsEdit) {
    return (
      <div className={cssClass} id={item.anchorName}
        data-cms-item-id={item.itemId}
        style={{ padding: '10px', background: '#eee', border: '1px solid #ccc' }}>
        <b>Flipbook:</b> {pdfPath || <em>(no PDF selected)</em>}
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className={cssClass} id={item.anchorName}>
        <em>Flipbook: no PDF selected.</em>
      </div>
    );
  }

  // Flex centring turns the leftover into an even margin above and below.
  const shellH = pageH ? pageH + gap * 2 + s.TOOLBAR_HEIGHT : 0;

  const ready = numPages > 0 && pageW > 0;
  // Remount when the geometry changes - StPageFlip cannot relayout in place.
  const bookKey = pageW + 'x' + pageH + 'x' + cols;

  const atStart = currentPage <= 0;
  const atEnd = !!numPages && currentPage >= numPages - 1;

  // In a spread the two visible pages are labelled together, as a book would be.
  let pageLabel = '…';
  if (numPages) {
    // Trust the column count we actually render, and let the library veto it
    // if it dropped to portrait on us.
    const spreadView = cols === 2 && orientation !== 'portrait';
    const single = !spreadView || atStart || atEnd;
    const right = Math.min(currentPage + 2, numPages);
    pageLabel = single
      ? (currentPage + 1) + ' / ' + numPages
      : (currentPage + 1) + '-' + right + ' / ' + numPages;
  }

  const shellElt = (
    <div className={cssClass} id={item.anchorName}
      ref={shellRef}
      tabIndex={0}
      style={(shellH && !fullscreen) ? { height: shellH + 'px' } : undefined}
      css={[s.shell, fullscreen && s.shellFullscreen, pseudoFullscreen && s.shellPseudoFullscreen]}>

      <div css={s.stage} ref={stageRef}>
        <div css={s.book}>
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoad}
            loading={
              <div css={s.loading}>
                <div css={s.spinner} role="progressbar" aria-label="Loading flipbook" />
                <span>Loading flipbook&hellip;</span>
              </div>
            }
            error={
              <div css={s.loading}>
                Sorry, this flipbook could not be loaded.
              </div>
            }
          >
            {ready && (
              <HTMLFlipBook
                key={bookKey}
                ref={flipRef}
                width={pageW}
                height={pageH}
                size="fixed"
                startPage={currentPage}
                maxShadowOpacity={0.5}
                showCover={showCover}
                usePortrait={!twoUp}
                mobileScrollSupport={true}
                onFlip={onFlip}
                onInit={onInit}
                onChangeState={onChangeState}
                onChangeOrientation={onChangeOrientation}
                flippingTime={700}
                drawShadow={true}
                showPageCorners={true}
                swipeDistance={30}
              >
                {pdfPages}
              </HTMLFlipBook>
            )}
          </Document>
        </div>
      </div>

      {numPages > 0 && (
        <div css={s.toolbar}>
          <div css={s.toolbarLeft}>
            <button type="button" css={s.btn} onClick={(e) => { goHome(); e.currentTarget.blur(); }}
              aria-label="First page" title="First page" disabled={atStart}>&#8676;</button>
            <span css={s.counter}>{pageLabel}</span>
          </div>
          <div css={s.toolbarRight}>
            <form css={s.jumpForm} onSubmit={submitPageInput}>
              <input css={s.jumpInput} type="text" inputMode="numeric" value={pageInput}
                onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="Go to" aria-label="Go to page" title="Go to page" />
            </form>
            <button type="button" css={s.btn} onClick={(e) => { flipPrev(); e.currentTarget.blur(); }}
              aria-label="Previous page" title="Previous" disabled={atStart}>&lsaquo;</button>
            <button type="button" css={s.btn} onClick={(e) => { flipNext(); e.currentTarget.blur(); }}
              aria-label="Next page" title="Next" disabled={atEnd}>&rsaquo;</button>
            <button type="button" css={s.btn} onClick={(e) => { toggleFullscreen(); e.currentTarget.blur(); }}
              aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              {fullscreen ? "⤢" : "⛶"}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // The portal is unconditional - only its container's parent moves. Toggling
  // between portalled and inline would change the element type at this
  // position, and React would tear the subtree down and reload the PDF.
  return (
    <React.Fragment>
      <div ref={anchorRef} />
      {portalEl && createPortal(shellElt, portalEl)}
    </React.Fragment>
  );
}
